import { useEffect, useState } from "react"
import { useQuery } from "@tanstack/react-query"
import { ClientAvatar } from "@/components/client-avatar"
import { consumePendingSwitch, type PendingContextSwitch } from "@/lib/context-switch"
import { api } from "@/lib/api"
import { useAuth } from "@/hooks/useAuth"
import { SwitchOverlayCard } from "@/components/switch-overlay-card"

/**
 * Full-screen overlay shown while the app switches client context
 * (Mercury-style account switch). Two phases:
 *  - "leaving":  mounted by the page that triggered the switch, right before
 *                beginContextSwitch() reloads the app.
 *  - "arriving": mounted globally on boot when a pending switch is found in
 *                sessionStorage; plays the destination card, then fades away.
 */
export function ContextSwitchOverlay({
  clientName,
  domain,
  phase,
  exiting = false,
  logoUrl = null,
}: {
  clientName: string
  domain: string | null
  phase: "leaving" | "arriving"
  exiting?: boolean
  /** Uploaded client logo — same asset the sidebar header shows top-left. */
  logoUrl?: string | null
}) {
  return (
    <SwitchOverlayCard
      phase={phase}
      exiting={exiting}
      title={clientName}
      visual={
        // Same component + fallback chain as the sidebar header's top-left icon:
        // uploaded logo -> site favicon -> initials gradient
        <ClientAvatar name={clientName} domain={domain} website={domain} logoUrl={logoUrl} size="lg" />
      }
    />
  )
}

/**
 * Read-and-clear once per page load, at module scope rather than inside the
 * component. Consuming destroys its own input, and StrictMode double-invokes
 * useState initializers in dev — the second call would find an empty slot and
 * swallow the arrival. A pending switch belongs to the page load, not to any
 * component instance, so this is where it belongs.
 */
const initialArrival = consumePendingSwitch()

/**
 * Mounted once at the app root. Completes the switch animation after the
 * context-switch reload: shows the destination card briefly, then fades out.
 */
export function ArrivalOverlayHost() {
  const [arrival, setArrival] = useState<PendingContextSwitch | null>(initialArrival)
  const [exiting, setExiting] = useState(false)
  const { currentLicense } = useAuth()

  // Fetch the destination client's uploaded logo — the exact asset the sidebar
  // header renders top-left. Same queryKey as SidebarClientSelector, so the
  // cache is shared. Gated until auth has installed the new license's headers,
  // because /settings/domain-rating is scoped to the active license.
  const { data: drData } = useQuery({
    queryKey: ["domain-rating", arrival?.licenseId],
    queryFn: () => api.settings.getDomainRating(),
    enabled: !!arrival && currentLicense?.id === arrival.licenseId,
    staleTime: 1000 * 60 * 60,
    retry: false,
  })

  useEffect(() => {
    if (!arrival) return
    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches
    const holdMs = reduceMotion ? 250 : 700
    const fadeTimer = setTimeout(() => setExiting(true), holdMs)
    const removeTimer = setTimeout(() => setArrival(null), holdMs + 550)
    return () => {
      clearTimeout(fadeTimer)
      clearTimeout(removeTimer)
    }
  }, [arrival])

  if (!arrival) return null

  return (
    <ContextSwitchOverlay
      clientName={arrival.clientName}
      domain={arrival.domain}
      phase="arriving"
      exiting={exiting}
      logoUrl={drData?.logo_url ?? null}
    />
  )
}
